import { useState } from "react";
import { Link } from "react-router-dom";
import SafeImage from "../SafeImage";
import DropAnalyticsModal from "./DropAnalyticsModal";

const STROKE = 1.8;

function formatViews(count = 0, max = 100) {
  const n = Number(count) || 0;
  const isOpen = n < max;
  return { count: n, max, isOpen, pct: Math.min(100, Math.round((n / max) * 100)) };
}

function contentTypeLabel(type) {
  const map = { video: "Video", photo: "Photo", audio: "Audio", article: "Article" };
  return map[type] || "Drop";
}

export default function CreatorDropCard({
  drop,
  featured = false,
  isOwner = false,
  nicheStyle = {},
}) {
  const views = formatViews(drop.viewCount ?? drop.views ?? 0, drop.maxViews || 100);
  const dropId = drop.id || drop.momentId;
  const thumb = drop.thumbnailUrl || drop.mediaUrl || drop.coverUrl || "";
  const price = drop.price != null ? Number(drop.price) : 0;
  const typeLabel = contentTypeLabel(drop.contentType || drop.type);
  const [analyticsOpen, setAnalyticsOpen] = useState(false);

  return (
    <div className={`cp-drop-card-wrap${featured ? " cp-drop-card-wrap--featured" : ""}`}>
      <Link
        to={`/drop/${dropId}`}
        className={`cp-drop-card${featured ? " cp-drop-card--featured" : ""}`}
        style={nicheStyle}
      >
        <div className="cp-dc-accent" />
        <div className="cp-dc-thumb">
          <SafeImage src={thumb} alt={drop.title || "Drop"} className="cp-dc-img" fallbackText="" />
          <div className="cp-dc-shade" />
          <span className="cp-dc-type">{typeLabel}</span>
          {views.isOpen && isOwner && (
            <span className="cp-dc-live-badge">
              <span className="cp-dc-live-dot" /> Live
            </span>
          )}
          <div className="cp-dc-chip">
            <div className={`cp-dc-num ${views.isOpen ? "cp-dc-num--open" : "cp-dc-num--closed"}`}>
              {views.count}
            </div>
            <div className="cp-dc-txt">
              <strong>of {views.max}</strong>
              {views.isOpen ? "still open" : "closed"}
            </div>
          </div>
          {!views.isOpen && !isOwner && price > 0 && (
            <div className="cp-locked-overlay">
              <span className="cp-lo-text">Drop closed</span>
              <span className="cp-lo-sub">Unlock for ${price.toFixed(2)} CAD</span>
            </div>
          )}
        </div>
        <div className="cp-dc-scar-bar">
          <div
            className={`cp-dc-scar-fill cp-dc-scar-fill--loaded ${views.isOpen ? "cp-dc-scar-fill--niche" : "cp-dc-scar-fill--closed"}`}
            style={{ "--w": `${views.pct}%` }}
          />
        </div>
        <div className="cp-dc-body">
          <div className={`cp-dc-title${!views.isOpen ? " cp-dc-title--closed" : ""}`}>
            {drop.title || "Untitled drop"}
          </div>
          <div className="cp-dc-foot">
            <span className={`cp-dc-pill ${views.isOpen ? "cp-dc-pill--open" : "cp-dc-pill--closed"}`}>
              {views.isOpen && <span className="cp-dc-pill-dot" />}
              {views.isOpen ? "Open" : "Closed"}
            </span>
            {isOwner && drop.earnings != null ? (
              <span className="cp-dc-earn">${Number(drop.earnings).toFixed(2)} earned</span>
            ) : price > 0 ? (
              <span className="cp-dc-price">${price.toFixed(2)}</span>
            ) : (
              <span className="cp-dc-price">Free</span>
            )}
          </div>
        </div>
        {isOwner && (
          <div className="cp-dc-owner-overlay">
            <div className="cp-dco-btns">
              <span className="cp-dco-btn cp-dcob-edit">View stats</span>
            </div>
            <span className="cp-dco-quick">{views.count} views · {views.isOpen ? "accepting viewers" : "locked"}</span>
          </div>
        )}
      </Link>

      {isOwner && (
        <>
          <button
            type="button"
            className="gt-analytics-btn"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setAnalyticsOpen(true);
            }}
          >
            📊 {typeLabel} Analytics
          </button>
          <DropAnalyticsModal
            open={analyticsOpen}
            onClose={() => setAnalyticsOpen(false)}
            drop={drop}
          />
        </>
      )}
    </div>
  );
}

export function DropFilterPills({ value, onChange, counts = {} }) {
  const filters = [
    { id: "all", label: "All" },
    { id: "open", label: "Open" },
    { id: "unlocked", label: "Unlocked" },
    { id: "closed", label: "Closed" },
  ];
  return (
    <div className="cp-dh-filters">
      {filters.map((f) => (
        <button
          key={f.id}
          type="button"
          className={`cp-df${value === f.id ? " cp-df--on" : ""}`}
          onClick={() => onChange(f.id)}
        >
          {f.label}
          {counts[f.id] != null ? ` · ${counts[f.id]}` : ""}
        </button>
      ))}
    </div>
  );
}

export function filterDrops(drops, filter) {
  if (filter === "all") return drops;
  return drops.filter((d) => {
    const views = Number(d.viewCount ?? d.views ?? 0);
    const max = d.maxViews || 100;
    const isOpen = views < max;
    if (filter === "open") return isOpen;
    if (filter === "closed") return !isOpen;
    return true;
  });
}

export { STROKE as ICON_STROKE };
